export interface WorkflowStep {
  id: string;
  type: "trigger" | "condition" | "action" | "delay";
  label: string;
  config: Record<string, unknown>;
}

export interface Workflow {
  id: string;
  name: string;
  status: "Active" | "Draft" | "Disabled";
  trigger: string;
  sources: string[];
  flowSteps: WorkflowStep[];
  leadsProcessed: number;
  qualified: number;
  avgResponseTime: string;
  createdAt: string;
}

export interface MessageTemplate {
  id: string;
  name: string;
  status: "Approved" | "Pending" | "Draft";
  language: string;
  message: string;
  variables: string[];
  usageCount: number;
}

export interface AutoResponderSource {
  id: string;
  name: string;
  color: string;
  enabled: boolean;
  locked: boolean;
  helper: string;
}

export interface WorkflowAnalytics {
  totalProcessed: number;
  autoQualified: number;
  droppedLost: number;
  avgQualificationTime: string;
  funnel: { label: string; count: number; color: string }[];
}

export const autoResponderSources: AutoResponderSource[] = [
  { id: "indiamart", name: "IndiaMart", color: "bg-orange-100 text-orange-700", enabled: true, locked: false, helper: "High volume source — recommended" },
  { id: "justdial", name: "JustDial", color: "bg-blue-100 text-blue-700", enabled: true, locked: false, helper: "High volume source — recommended" },
  { id: "tradeindia", name: "TradeIndia", color: "bg-teal-100 text-teal-700", enabled: false, locked: false, helper: "Low volume — optional" },
  { id: "website", name: "Website Forms", color: "bg-indigo-100 text-indigo-700", enabled: true, locked: false, helper: "Website inquiries qualify well" },
  { id: "social", name: "Social Media", color: "bg-pink-100 text-pink-700", enabled: false, locked: false, helper: "Social leads vary in quality" },
  { id: "manual", name: "Manual Entry", color: "bg-gray-100 text-gray-700", enabled: false, locked: true, helper: "Manual leads skip auto-responder" },
];

export const workflows: Workflow[] = [
  {
    id: "wf-1",
    name: "WhatsApp Lead Qualification",
    status: "Active",
    trigger: "New Lead Created",
    sources: ["IndiaMart", "JustDial", "Website Forms"],
    flowSteps: [
      { id: "s1", type: "trigger", label: "Lead Created", config: { sources: ["IndiaMart", "JustDial", "Website Forms"] } },
      { id: "s2", type: "action", label: "Send WhatsApp", config: { template: "greeting_template" } },
      { id: "s3", type: "delay", label: "Wait 2 hours", config: { duration: 2, unit: "hours" } },
      { id: "s4", type: "condition", label: "Check Reply", config: { field: "reply", operator: "equals", value: "1" } },
      { id: "s5", type: "action", label: "Mark Qualified", config: { stage: "Qualified" } },
      { id: "s6", type: "action", label: "Assign to Agent", config: { method: "rules" } },
    ],
    leadsProcessed: 234,
    qualified: 156,
    avgResponseTime: "8 mins",
    createdAt: "2026-04-15",
  },
  {
    id: "wf-2",
    name: "No Reply Follow-up",
    status: "Active",
    trigger: "No reply after 2 hours",
    sources: ["IndiaMart", "JustDial", "Website Forms"],
    flowSteps: [
      { id: "s1", type: "trigger", label: "No Reply 2hrs", config: { condition: "no_reply_2h" } },
      { id: "s2", type: "action", label: "Send Follow-up", config: { template: "follow_up_template" } },
      { id: "s3", type: "delay", label: "Wait 24 hours", config: { duration: 24, unit: "hours" } },
      { id: "s4", type: "condition", label: "Still No Reply?", config: { field: "reply", operator: "is_empty" } },
      { id: "s5", type: "action", label: "Assign with Alert", config: { alert: true } },
    ],
    leadsProcessed: 89,
    qualified: 45,
    avgResponseTime: "3 hrs",
    createdAt: "2026-05-02",
  },
  {
    id: "wf-3",
    name: "High Value Lead Fast Track",
    status: "Draft",
    trigger: "Lead value > ₹50,000",
    sources: ["All Sources"],
    flowSteps: [
      { id: "s1", type: "trigger", label: "High Value Lead", config: { minValue: 50000 } },
      { id: "s2", type: "action", label: "Skip Bot", config: {} },
      { id: "s3", type: "action", label: "Assign Senior Agent", config: { role: "senior" } },
      { id: "s4", type: "action", label: "WhatsApp from Agent", config: { from: "agent" } },
    ],
    leadsProcessed: 0,
    qualified: 0,
    avgResponseTime: "—",
    createdAt: "2026-06-10",
  },
];

export const messageTemplates: MessageTemplate[] = [
  {
    id: "tpl-1",
    name: "Initial Greeting",
    status: "Approved",
    language: "English",
    message: "Hi {lead_name}! 👋\nThank you for your inquiry.\nAre you still looking for {property_type}?\n\nReply with:\n*1* - Yes, still interested\n*2* - Not looking anymore\n*3* - Please call me",
    variables: ["lead_name", "property_type"],
    usageCount: 2,
  },
  {
    id: "tpl-2",
    name: "24hr Follow-up",
    status: "Approved",
    language: "English",
    message: "Hi {lead_name}, this is {agent_name} from {company}.\n\nWe noticed you haven't responded to our earlier message.\n\nAre you still interested? We have some exciting options that match your requirements! 🏠",
    variables: ["lead_name", "agent_name", "company"],
    usageCount: 1,
  },
  {
    id: "tpl-3",
    name: "Qualification Questions",
    status: "Draft",
    language: "English",
    message: "Great to hear from you! 😊\nTo help you better, could you share:\n\n1️⃣ Your budget range?\n2️⃣ Preferred location/area?\n3️⃣ When are you planning to buy?\n\nWe'll find the perfect match!",
    variables: [],
    usageCount: 0,
  },
];

export const workflowAnalytics: WorkflowAnalytics = {
  totalProcessed: 323,
  autoQualified: 201,
  droppedLost: 78,
  avgQualificationTime: "4.2 hrs",
  funnel: [
    { label: "Leads Entered Workflow", count: 323, color: "indigo" },
    { label: "WhatsApp Sent", count: 298, color: "blue" },
    { label: "Reply Received", count: 234, color: "teal" },
    { label: "Interested (1)", count: 201, color: "green" },
    { label: "Not Interested (2)", count: 33, color: "red" },
    { label: "No Reply", count: 64, color: "amber" },
    { label: "Manually Assigned", count: 64, color: "gray" },
  ],
};

export const workflowStepLibrary = {
  triggers: [
    { id: "t-new-lead", icon: "ri-inbox-unarchive-line", label: "New Lead Created", color: "purple" },
    { id: "t-stage-change", icon: "ri-git-branch-line", label: "Lead Stage Changed", color: "purple" },
    { id: "t-score-change", icon: "ri-star-line", label: "Score Changed", color: "purple" },
    { id: "t-missed-call", icon: "ri-phone-line", label: "Missed Call", color: "purple" },
    { id: "t-whatsapp-reply", icon: "ri-whatsapp-line", label: "WhatsApp Reply Received", color: "purple" },
    { id: "t-time-delay", icon: "ri-timer-line", label: "Time Delay Triggered", color: "purple" },
  ],
  conditions: [
    { id: "c-source", icon: "ri-filter-line", label: "Check Source", color: "blue" },
    { id: "c-priority", icon: "ri-flag-line", label: "Check Priority", color: "blue" },
    { id: "c-score-range", icon: "ri-star-smile-line", label: "Check Score Range", color: "blue" },
    { id: "c-reply-content", icon: "ri-message-2-line", label: "Check Reply Content", color: "blue" },
    { id: "c-time-of-day", icon: "ri-time-line", label: "Check Time of Day", color: "blue" },
    { id: "c-lead-value", icon: "ri-money-rupee-circle-line", label: "Check Lead Value", color: "blue" },
  ],
  actions: [
    { id: "a-send-whatsapp", icon: "ri-whatsapp-line", label: "Send WhatsApp Message", color: "green" },
    { id: "a-assign", icon: "ri-user-add-line", label: "Assign to Agent", color: "green" },
    { id: "a-update-stage", icon: "ri-git-branch-line", label: "Update Lead Stage", color: "green" },
    { id: "a-update-score", icon: "ri-star-line", label: "Update Lead Score", color: "green" },
    { id: "a-schedule-call", icon: "ri-phone-line", label: "Schedule Call", color: "green" },
    { id: "a-wait-delay", icon: "ri-timer-line", label: "Wait / Delay", color: "green" },
    { id: "a-notify", icon: "ri-notification-3-line", label: "Send Notification", color: "green" },
    { id: "a-mark-lost", icon: "ri-close-circle-line", label: "Mark as Lost", color: "green" },
    { id: "a-mark-qualified", icon: "ri-check-double-line", label: "Mark as Qualified", color: "green" },
  ],
};

export const funnelBarColors: Record<string, string> = {
  indigo: "bg-indigo-500",
  blue: "bg-blue-500",
  teal: "bg-teal-500",
  green: "bg-green-500",
  red: "bg-red-500",
  amber: "bg-amber-500",
  gray: "bg-gray-400",
};